import { Investment, InvestmentResults } from '../types';
import { getDetailedCertificateEvolution } from './calculations/certificateEvolution';

export function calculateResults(investment: Investment): InvestmentResults {
  try {
    // Get detailed certificate evolution
    const certificateEvolution = getDetailedCertificateEvolution(investment);
    
    if (!certificateEvolution || certificateEvolution.length === 0) {
      throw new Error('No certificate evolution data available');
    }

    // Get final year data
    const finalYearData = certificateEvolution[certificateEvolution.length - 1];
    
    // Calculate basic metrics
    const initialInvestment = investment.initialCertificates * investment.certificateBasePrice;
    const finalPatrimony = finalYearData.citrusPatrimony;
    const finalMonthlyIncome = finalYearData.citrusIncome / 12;
    
    // Calculate certificates summary
    const certificatesSummary = {
      initialCertificates: investment.initialCertificates,
      fromReinvestment: finalYearData.certificatesFromReinvestment,
      totalCertificates: finalYearData.totalCertificates
    };
    
    // Calculate performance metrics
    const capitalMultiplier = finalPatrimony / initialInvestment;
    const years = investment.years;
    const cagr = Math.pow(finalPatrimony / initialInvestment, 1 / years) - 1;
    
    // Calculate IRR (simplified)
    const irr = cagr; // Simplified calculation
    
    // Find payback year
    let paybackYear: number | null = null;
    let cumulativeCashFlow = -initialInvestment;
    
    for (const yearData of certificateEvolution) {
      cumulativeCashFlow += yearData.totalUtility;
      if (cumulativeCashFlow > 0 && !paybackYear) {
        paybackYear = yearData.year;
        break;
      }
    }
    
    // Calculate comparative investments
    const cetesPatrimony = initialInvestment * Math.pow(1 + investment.cetesRate / 100, years);
    const savingsPatrimony = initialInvestment * Math.pow(1 + investment.savingsRate / 100, years);
    const realEstatePatrimony = initialInvestment * Math.pow(1 + investment.realEstateAppreciation / 100, years);
    
    // Calculate real values (inflation-adjusted)
    const inflationFactor = Math.pow(1 + investment.inflationRate / 100, years);
    const realFinalPatrimony = finalPatrimony / inflationFactor;
    const realCetesPatrimony = cetesPatrimony / inflationFactor;
    const realSavingsPatrimony = savingsPatrimony / inflationFactor;
    const realRealEstatePatrimony = realEstatePatrimony / inflationFactor;
    
    // Calculate EBITDA value
    const finalEbitdaValue = finalYearData.totalUtility * investment.ebitdaFactor;
    
    // Calculate total cash out if partial cash out is enabled
    const totalCashOut = investment.partialCashOut ? 
      certificateEvolution.reduce((sum, data) => sum + data.yearlyCashOutAmount, 0) : 0;
    
    // Calculate calculated annual profit percentage
    const calculatedAnnualProfitPercentage = 
      (investment.averageProductionPerHectare * investment.averageSalePricePerKg * 0.1 * 0.65 / investment.certificateBasePrice) * 100;
    
    // Calculate first year utility and price differential for long-term calculator
    const firstYearUtility = investment.isLongTermCalculator ? 
      (investment.averageProductionPerHectare * investment.averageSalePricePerKg * 0.1 * 0.65) : 0;
    const priceDifferential = investment.isLongTermCalculator ? 
      investment.certificateBasePrice * 0.25 : 0;
    
    // Prepare yearly data
    const yearlyData = certificateEvolution.map(data => ({
      year: data.year,
      citrusPatrimony: data.citrusPatrimony,
      citrusIncome: data.citrusIncome,
      certificatesFromReinvestment: data.certificatesFromReinvestment,
      reinvestmentFund: data.reinvestmentFund,
      availableCashFlow: data.totalUtility - (data.yearlyCashOutAmount || 0),
      partialCashOutAmount: data.yearlyCashOutAmount,
      yearlyReinvestmentContribution: data.yearlyReinvestmentContribution,
      cumulativeReinvestmentContribution: data.cumulativeReinvestmentContribution,
      cumulativeTotalUtility: data.cumulativeTotalUtility,
      contributedCapital: initialInvestment,
      
      // Calculate comparative investments for this year
      cetesPatrimony: initialInvestment * Math.pow(1 + investment.cetesRate / 100, data.year),
      savingsPatrimony: initialInvestment * Math.pow(1 + investment.savingsRate / 100, data.year),
      realEstatePatrimony: initialInvestment * Math.pow(1 + investment.realEstateAppreciation / 100, data.year),
      
      // Calculate income from comparative investments
      cetesIncome: (initialInvestment * Math.pow(1 + investment.cetesRate / 100, data.year)) * (investment.cetesRate / 100),
      savingsIncome: (initialInvestment * Math.pow(1 + investment.savingsRate / 100, data.year)) * (investment.savingsRate / 100),
      realEstateIncome: (initialInvestment * Math.pow(1 + investment.realEstateAppreciation / 100, data.year)) * (investment.realEstateRent / 100),
      
      // Calculate taxes if applicable
      cetesTaxes: investment.applyTaxes ? 
        (initialInvestment * Math.pow(1 + investment.cetesRate / 100, data.year)) * (investment.cetesRate / 100) * (investment.taxRate / 100) : 0,
      savingsTaxes: investment.applyTaxes ? 
        (initialInvestment * Math.pow(1 + investment.savingsRate / 100, data.year)) * (investment.savingsRate / 100) * (investment.taxRate / 100) : 0,
      realEstateTaxes: investment.applyTaxes ? 
        (initialInvestment * Math.pow(1 + investment.realEstateAppreciation / 100, data.year)) * (investment.realEstateRent / 100) * (investment.taxRate / 100) : 0,
      
      // Calculate real values (inflation-adjusted)
      realCitrusPatrimony: data.citrusPatrimony / Math.pow(1 + investment.inflationRate / 100, data.year),
      realCetesPatrimony: (initialInvestment * Math.pow(1 + investment.cetesRate / 100, data.year)) / Math.pow(1 + investment.inflationRate / 100, data.year),
      realSavingsPatrimony: (initialInvestment * Math.pow(1 + investment.savingsRate / 100, data.year)) / Math.pow(1 + investment.inflationRate / 100, data.year),
      realEstateRealPatrimony: (initialInvestment * Math.pow(1 + investment.realEstateAppreciation / 100, data.year)) / Math.pow(1 + investment.inflationRate / 100, data.year),
      
      // Calculate real income
      realCitrusIncome: data.citrusIncome / Math.pow(1 + investment.inflationRate / 100, data.year),
      realCetesIncome: ((initialInvestment * Math.pow(1 + investment.cetesRate / 100, data.year)) * (investment.cetesRate / 100)) / Math.pow(1 + investment.inflationRate / 100, data.year),
      realSavingsIncome: ((initialInvestment * Math.pow(1 + investment.savingsRate / 100, data.year)) * (investment.savingsRate / 100)) / Math.pow(1 + investment.inflationRate / 100, data.year),
      realEstateRealIncome: ((initialInvestment * Math.pow(1 + investment.realEstateAppreciation / 100, data.year)) * (investment.realEstateRent / 100)) / Math.pow(1 + investment.inflationRate / 100, data.year),
      
      // EBITDA value
      ebitdaValue: data.totalUtility * investment.ebitdaFactor,
      
      // Cash flow calculations
      cetesCashFlow: data.year === 1 ? -initialInvestment + ((initialInvestment * Math.pow(1 + investment.cetesRate / 100, data.year)) * (investment.cetesRate / 100)) : 
        ((initialInvestment * Math.pow(1 + investment.cetesRate / 100, data.year)) * (investment.cetesRate / 100)),
      savingsCashFlow: data.year === 1 ? -initialInvestment + ((initialInvestment * Math.pow(1 + investment.savingsRate / 100, data.year)) * (investment.savingsRate / 100)) : 
        ((initialInvestment * Math.pow(1 + investment.savingsRate / 100, data.year)) * (investment.savingsRate / 100)),
      realEstateCashFlow: data.year === 1 ? -initialInvestment + ((initialInvestment * Math.pow(1 + investment.realEstateAppreciation / 100, data.year)) * (investment.realEstateRent / 100)) : 
        ((initialInvestment * Math.pow(1 + investment.realEstateAppreciation / 100, data.year)) * (investment.realEstateRent / 100)),
      
      // Additional fields for partial cash out
      yearCashOutPercentage: investment.partialCashOut ? 
        (investment.yearlyCashOutPercentages && investment.yearlyCashOutPercentages[data.year - 1] !== undefined ? 
          investment.yearlyCashOutPercentages[data.year - 1] : investment.cashOutPercentage) : 0
    }));
    
    // Calculate passive income efficiency
    const passiveIncomeEfficiency = finalMonthlyIncome / (finalPatrimony * 0.01); // Efficiency metric
    
    return {
      finalMonthlyIncome,
      finalPatrimony,
      capitalMultiplier,
      cagr,
      irr,
      paybackYear,
      certificatesSummary,
      passiveIncomeEfficiency,
      cetesPatrimony,
      savingsPatrimony,
      realEstatePatrimony,
      realFinalPatrimony,
      realCetesPatrimony,
      realSavingsPatrimony,
      realRealEstatePatrimony,
      finalEbitdaValue,
      totalCashOut,
      calculatedAnnualProfitPercentage,
      firstYearUtility,
      priceDifferential,
      yearlyData
    };
  } catch (error) {
    console.error('Error in calculateResults:', error);
    throw error;
  }
}